import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  inject,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { Router } from "@angular/router";
import Swal from "sweetalert2";

import { ApiService } from "../../../shared/services/api/api.service";
import { API_ENDPOINTS } from "../../../shared/services/api/api-endpoints";
import { WebSocketsService } from "../../../shared/services/web-sockets.service";
import { environment } from "../../../../environments/environment";
import { LogoHeaderComponent } from "../../../shared/components/logo-header/logo-header.component";

@Component({
  selector: "app-verify-otp",
  standalone: true,
  imports: [CommonModule, FormsModule, LogoHeaderComponent],
  templateUrl: "./verify-otp-page.component.html",
  styleUrls: ["./verify-otp-page.component.scss"],
})
export class VerifyOtpComponent implements OnInit, OnChanges {
  public router = inject(Router);
  private ApiService = inject(ApiService);
  private wsService = inject(WebSocketsService);

  @Input() OtpFromModal = false;
  @Input() modalEmail = "";

  @Output() verified = new EventEmitter<any>();
  @Output() backToLogin = new EventEmitter<void>();

  baseUrl = environment.baseUrl;
  notifUrl = environment.notifUrl;
  assetUrl = environment.assetBaseUrl;

  otp: string[] = ["", "", "", "", "", ""];
  isLoading = false;
  email = "";
  errorMessage = "";

  ngOnInit(): void {
    this.resolveEmail();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["modalEmail"] || changes["OtpFromModal"]) {
      this.resolveEmail();
    }
  }

  private resolveEmail(): void {
    if (this.OtpFromModal) {
      this.email = this.modalEmail || "";

      // In modal mode, never navigate away just because email is not ready yet.
      return;
    }

    this.email = sessionStorage.getItem("otp_email") || "";

    if (!this.email) {
      Swal.fire({
        title: "Error",
        text: "Please login first",
        icon: "error",
        confirmButtonText: "OK",
      });

      this.router.navigateByUrl("/login");
    }
  }

  handleInputChange(index: number, event: Event): void {
    const input = event.target as HTMLInputElement;
    const inputValue = input.value.replace(/[^0-9]/g, "");

    if (inputValue.length > 1) {
      return;
    }

    this.otp[index] = inputValue;

    if (index < 5 && inputValue) {
      setTimeout(() => {
        const nextInput = document.getElementById(`otp-input-${index + 1}`);
        nextInput?.focus();
      }, 0);
    }
  }

  handleKeyDown(index: number, event: KeyboardEvent): void {
    if (event.key === "Backspace" && !this.otp[index] && index > 0) {
      const prevInput = document.getElementById(
        `otp-input-${index - 1}`,
      ) as HTMLInputElement | null;

      if (prevInput) {
        prevInput.focus();
        prevInput.value = "";
        this.otp[index - 1] = "";
      }
    }
  }

  handlePaste(event: ClipboardEvent): void {
    const pasted = event.clipboardData
      ?.getData("text")
      .replace(/\D/g, "")
      .slice(0, 6);

    if (!pasted) {
      return;
    }

    event.preventDefault();

    this.otp = ["", "", "", "", "", ""];

    pasted.split("").forEach((digit, index) => {
      this.otp[index] = digit;
    });

    const targetIndex = Math.min(pasted.length, 6) - 1;

    setTimeout(() => {
      document.getElementById(`otp-input-${targetIndex}`)?.focus();
    }, 0);
  }

  handleSubmit(event: Event): void {
    event.preventDefault();

    const otpValue = this.otp.join("");

    if (!this.email) {
      this.errorMessage = "Missing email address. Please return to login.";
      return;
    }

    if (otpValue.length !== 6) {
      this.errorMessage = "Please enter all 6 digits";
      return;
    }

    this.isLoading = true;
    this.errorMessage = "";

    const verifyData = {
      email: this.email,
      otp: otpValue,
    };

    this.ApiService.request(
      API_ENDPOINTS.auth.verifyOtp,
      "POST",
      verifyData,
      "Login successful!",
      undefined,
      { skipAuth: true },
    ).subscribe({
      next: (response: any) => {
        console.log("✅ Code verified successfully:", response);

        this.isLoading = false;

        localStorage.setItem("access_token", response.access);

        this.wsService.connect(
          `${this.notifUrl}notifications/?token=${response.access}`,
        );

        sessionStorage.removeItem("otp_email");

        Swal.fire({
          title: "Success",
          text: "Login successful!",
          icon: "success",
          timer: 1600,
          showConfirmButton: false,
          toast: true,
          position: "top-end",
        });

        if (this.OtpFromModal) {
          // Preserve appointment modal behavior.
          this.verified.emit(response);
          return;
        }

        // Redirect based on authenticated user permissions.
        this.redirectAfterLogin();
      },

      error: (err) => {
        console.error("❌ Code verification failed:", err);

        this.isLoading = false;

        if (err.error?.detail) {
          this.errorMessage = err.error.detail;
        } else {
          this.errorMessage = "Invalid Code. Please try again.";
        }

        this.otp = ["", "", "", "", "", ""];

        setTimeout(() => {
          document.getElementById("otp-input-0")?.focus();
        }, 0);
      },
    });
  }

  private redirectAfterLogin(): void {
    // Retrieve the authenticated user's identity and permissions.
    // No need to call a protected Platform Admin endpoint.

    this.ApiService.request<any>(
      API_ENDPOINTS.auth.me,
      "GET",
    ).subscribe({
      next: (response: any) => {
        const user =
          response?.data?.user ??
          response?.data ??
          response?.user ??
          response;

        const role = String(
          user?.role_name ?? user?.role ?? "",
        )
          .trim()
          .toLowerCase();

        const isPlatformAdmin =
          user?.is_platform_admin === true;

        // =====================================================
        // PLATFORM ADMINISTRATOR
        // =====================================================

        // This flag is calculated by Django using the same
        // authorization rules as IsPlatformAdmin.
        if (isPlatformAdmin) {
          this.router.navigateByUrl(
            "/admin/platform/overview",
          );
          return;
        }

        // =====================================================
        // BUSINESS OWNER
        // =====================================================

        if (role === "business_owner") {
          this.router.navigateByUrl(
            "/admin/business/overview",
          );
          return;
        }

        // =====================================================
        // STAFF
        // =====================================================

        if (role === "staff") {
          this.router.navigateByUrl(
            "/admin/staff/overview",
          );
          return;
        }

        // =====================================================
        // CUSTOMER
        // =====================================================

        if (role === "customer") {
          this.router.navigateByUrl(
            "/admin/customer/overview",
          );
          return;
        }

        // =====================================================
        // UNKNOWN ROLE
        // =====================================================

        console.error(
          "Unknown application role:",
          role,
        );

        this.errorMessage =
          "Your account does not have an assigned application role.";
      },

      error: (error) => {
        console.error(
          "Failed to retrieve authenticated user profile:",
          error,
        );

        this.errorMessage =
          "Login succeeded, but we couldn't retrieve your account permissions.";
      },
    });
  }

  resendOtp(): void {
    if (!this.email) {
      return;
    }

    this.ApiService.request(
      API_ENDPOINTS.auth.resendOtp,
      "POST",
      { email: this.email },
      "Verification code resent successfully!",
      undefined,
      { skipAuth: true },
    ).subscribe({
      next: (response: any) => {
        console.log("✅ Verification code resent:", response);

        this.otp = ["", "", "", "", "", ""];

        Swal.fire({
          title: "Code sent",
          text: "A new verification code was sent to your email.",
          icon: "success",
          timer: 1800,
          showConfirmButton: false,
          toast: true,
          position: "top-end",
        });

        setTimeout(() => {
          document.getElementById("otp-input-0")?.focus();
        }, 0);
      },

      error: (err) => {
        console.error("❌ Failed to resend verification code:", err);

        Swal.fire({
          title: "Error",
          text: "Failed to resend verification code. Please try again.",
          icon: "error",
          timer: 3000,
          showConfirmButton: false,
          toast: true,
          position: "top-end",
        });
      },
    });
  }

  goBack(): void {
    if (this.OtpFromModal) {
      this.backToLogin.emit();
      return;
    }

    this.router.navigateByUrl("/login");
  }

  get isComplete(): boolean {
    return this.otp.every((digit) => digit !== "");
  }

  trackByIndex(index: number): number {
    return index;
  }
}
